import Link from 'next/link';
import { ArrowUpRight, Check, X } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useBasePath } from '../../../../lib/use-base-path';
import { Button, Card, IconButton, Typography } from '../../ui';
import styles from './MapInfoCard.module.css';

/** What a selection says: the eyebrow (its region), a title, a line of context, the text, and ONE action. */
export interface MapCardContent {
  /** A stable key for the thing described (a node id, an edge key, an event id). */
  key: string;
  region: string;
  title: string;
  /** A secondary line: the time, the agent, a status such as "Not connected". */
  meta?: string;
  text?: string;
  /** The card's action — every object on the map has one (open, manage, connect, read…). `done` renders it
   *  as already taken (a vote that was cast) with the label it then reads. */
  action?: { label: string; href?: string; onClick?: () => void; done?: boolean };
  /** Extra lines under the text — a phone number, a list of groups — shown as they are. */
  lines?: string[];
  /** A quieter second link, e.g. "Read more" beside a primary "Open task". */
  link?: { label: string; href?: string; onClick?: () => void };
}

/**
 * The info card that opens on a node, a wire or a particle (Figma "Info card", 491:196873): a small DS Card
 * floated beside the thing, naming it, what last happened there, and what the reader can do about it. HTML
 * over the canvas rather than a `<foreignObject>`, so it keeps the app's type and never scales with the zoom.
 */
export function MapInfoCard({
  content,
  anchor,
  viewport,
  onClose,
}: {
  content: MapCardContent;
  anchor: { x: number; y: number };
  viewport: { w: number; h: number };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const W = 272;
  const H = 190;
  // Beside the anchor, flipping to whichever side has room; clamped inside the host.
  const left = anchor.x + 28 + W <= viewport.w ? anchor.x + 28 : Math.max(8, anchor.x - 28 - W);
  const top = Math.min(Math.max(8, anchor.y - H / 2), Math.max(8, viewport.h - H - 8));
  const hrefOf = (h?: string) => (h ? (h.startsWith('http') ? h : `${basePath}${h}`) : undefined);

  return (
    <Card className={styles.card} style={{ left, top, width: W }} role="group" aria-label={content.title} data-node={content.key}>
      <div className={styles.head}>
        <Typography variant="p3" color="secondary" className={styles.region}>
          {content.region}
        </Typography>
        <IconButton size="tiny" variant="text" onClick={onClose} aria-label={t('common.close')}>
          <X size={16} aria-hidden />
        </IconButton>
      </div>
      <Typography variant="h4" color="primary" component="h3" className={styles.title}>
        {content.title}
      </Typography>
      {content.meta && (
        <Typography variant="p3" color="secondary" className={styles.meta}>
          {content.meta}
        </Typography>
      )}
      {/* Only what happened, when something did: a placeholder sentence on every quiet card said nothing. */}
      {content.text ? (
        <Typography variant="p2" color="primary" className={styles.text} dir="auto">
          {content.text}
        </Typography>
      ) : null}
      {content.lines?.length ? (
        <ul className={styles.lines}>
          {content.lines.map((line, i) => (
            <li key={i}>
              <Typography variant="p3" color="secondary" component="span" dir="auto">
                {line}
              </Typography>
            </li>
          ))}
        </ul>
      ) : null}
      <div className={styles.actions}>
        {content.action?.done ? (
          <Button size="small" variant="outlined" disabled startIcon={<Check size={16} aria-hidden />}>
            {content.action.label}
          </Button>
        ) : null}
        {content.action &&
          !content.action.done &&
          (content.action.href ? (
            <Button
              size="small"
              href={content.action.href}
              // Anything that is not an in-app path (https, sms:, wa.me) is external and leaves in a new tab.
              external={!content.action.href.startsWith('/')}
              {...(!content.action.href.startsWith('/') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              onClick={content.action.onClick}
            >
              {content.action.label}
            </Button>
          ) : (
            <Button size="small" onClick={content.action.onClick}>
              {content.action.label}
            </Button>
          ))}
        {content.link && (
          <Link href={content.link.href ?? '#'} as={hrefOf(content.link.href)} className={styles.more} onClick={content.link.onClick}>
            {content.link.label}
            <ArrowUpRight size={14} aria-hidden />
          </Link>
        )}
      </div>
    </Card>
  );
}
